import { type GraphQLFormattedError } from 'graphql';

import {
  type AttributionInput,
  type BootcampOrderInput,
  type ClassOrderInput,
  type HourlyOrderInput,
  LelandErrorCode,
  type PackageOrderInput,
  type RecurringCadence,
  type Scalars,
  type SkuTier,
} from '../__generated-gql-types__/globalTypes';

import { LelandSessionStorage } from './storage';
import {
  type ValidationError,
  ValidationErrorsHash,
} from './validation-errors';

const ORDER_STORAGE_LOCATION = 'order';
const SESSION_STORAGE_LOCATION = 'session';

export interface CheckoutFormValues {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  cardElement: undefined; // Only used to support TS
  numberOfCharges: number;
  categoryUrn: Possible<Scalars['CategoryUrn']['output']>;
  lelandPlusOptOut: boolean;
  /**
   * Field used for setting global checkout error
   */
  globalError?: string;
}

export interface LocalOrderInputs {
  coachUrn: Scalars['CoachUrn']['output'];
  tier?: Possible<SkuTier>;
  packageOrder?: PackageOrderInput;
  hourlyOrder?: HourlyOrderInput;
  subscriptionOrder?: Scalars['SkuUrn']['output'];
  bootcampOrder?: BootcampOrderInput;
  classOrder?: ClassOrderInput;
  categoryId: Possible<Scalars['CategoryUrn']['output']>;
  orderId?: Possible<string>;
  unassignedOrderId?: string;
  isFreeTrial?: boolean;
  recommendedOfferingUrn?: Possible<
    Scalars['RecommendedOfferingUrn']['output']
  >;
  note?: string;
  attribution?: Possible<AttributionInput>;
}

export interface LocalCheckoutSessionInputs {
  selectedTime: Possible<number>;
  sessionLength: number;
  sessionCoachId: Scalars['CoachUrn']['output'];
  coachingSessionUrn?: Possible<Scalars['CoachingSessionUrn']['output']>;
  recurringCadence: Possible<RecurringCadence>;
}

export const setLocalCheckoutOrderInputs = (order: LocalOrderInputs) =>
  LelandSessionStorage.setItem(ORDER_STORAGE_LOCATION, order);

export const getLocalCheckoutOrderInputs = (): Nullable<LocalOrderInputs> =>
  LelandSessionStorage.getItem<LocalOrderInputs>(ORDER_STORAGE_LOCATION);

export const clearLocalCheckoutOrderInputs = () =>
  LelandSessionStorage.removeItem(ORDER_STORAGE_LOCATION);

export const setLocalCheckoutSessionInputs = (
  session: LocalCheckoutSessionInputs,
) => LelandSessionStorage.setItem(SESSION_STORAGE_LOCATION, session);

export const getLocalCheckoutSessionInputs =
  (): Nullable<LocalCheckoutSessionInputs> =>
    LelandSessionStorage.getItem<LocalCheckoutSessionInputs>(
      SESSION_STORAGE_LOCATION,
    );

export const clearLocalCheckoutSessionInputs = () =>
  LelandSessionStorage.removeItem(SESSION_STORAGE_LOCATION);

const valueAsNumberEmptyIsZero = (value: string) => {
  if (value === '') {
    return 0;
  }
  return parseInt(value);
};

export const BUY_COACHING_REGISTER_OPTIONS = Object.freeze({
  TIME_IN_MINUTE: {
    setValueAs: valueAsNumberEmptyIsZero,
    min: 0,
    max: 100 * 60,
  },
});

// Remove period from end of Stripe validation error message for consistency
export const formatStripeErrorMessage = (message: string): string => {
  if (message.endsWith('.')) {
    return message.slice(0, message.length - 1);
  }

  return message;
};

const CREDIT_DEBIT_CARD_ERROR: ValidationError<CheckoutFormValues> = {
  field: 'cardElement',
  message: 'Card must be an unexpired credit/debit card for multiple payments.',
};

const CHECKOUT_ERRORS: Record<
  string | LelandErrorCode,
  ValidationError<CheckoutFormValues>
> = {
  'numberOfCharges: CREDIT_DEBIT_CARD_NEEDED': CREDIT_DEBIT_CARD_ERROR,
  CREDIT_DEBIT_CARD_NEEDED: CREDIT_DEBIT_CARD_ERROR,
  [LelandErrorCode.EMAIL_IN_USE]: {
    field: 'email',
    message: 'Email already in use. Try logging in.',
  },
  [LelandErrorCode.FIRST_NAME_MISSING]: {
    field: 'firstName',
    message: 'Name cannot be empty',
  },
  [LelandErrorCode.LAST_NAME_MISSING]: {
    field: 'lastName',
    message: 'Name cannot be empty',
  },
  [LelandErrorCode.VERSION_CONFLICT]: {
    field: 'globalError',
    message:
      'There is a new version of this package. Return to the listing page to proceed.',
  },
  [LelandErrorCode.CLASS_FULL]: {
    field: 'globalError',
    message: 'This class is full',
  },
};
export const deserializeCheckoutErrors = (
  errors?: readonly GraphQLFormattedError[],
): ValidationErrorsHash<CheckoutFormValues> =>
  (errors ?? []).reduce((validationErrorsHash, error) => {
    const validationError =
      CHECKOUT_ERRORS[(error.extensions?.errorCode ?? '') as string] ??
      CHECKOUT_ERRORS[(error.extensions?.message ?? '') as string];
    if (validationError) {
      validationErrorsHash.addError(validationError);
    }
    return validationErrorsHash;
  }, new ValidationErrorsHash<CheckoutFormValues>());
